import React, { useState, useRef, useEffect, Fragment, useCallback, useContext, useMemo } from 'react';
import { Row, Col, Spinner, OverlayTrigger, Tooltip, Button, ButtonToolbar, Badge } from 'react-bootstrap';
import StatusBar from '@components/StatusBar';
import { getBatchesByInfo, downloadDocument } from '@lib/api';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import '@ag-grid-community/core/dist/styles/ag-theme-alpine.css';
import AG_GRID_LOCALE_IT from '@components/ag-grid/locale-it';
import { IcoDanger, IcoDownloadFile, IcoReplaced, IcoArrowClockwise } from '@icons';
import Unauthorized from 'components/Unauthorized';
import { columnCentered } from '@lib/helpers/agGridHelpers';
import { translate } from 'components/translation/translation';
import { OETrans } from 'components/translation/OETrans';
import { dateFormatter } from '@lib/helpers/formatting';
import { UserContext } from 'components/contexts/UserProvider';
import { Loading } from '@components/atomic/atoms';
import SubTabs from '@components/atomic/molecules/SubTabs/SubTabs';
import DownloadEntitiesModal from './DownloadEntitiesModal';
import UploadEntitiesModal from './UploadEntitiesModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import EntitiesTable from './EntitiesTable';
import EntitiesStagingTable from './EntitiesStagingTable';
import ChecklistsStagingTable from './ChecklistsStagingTable';
import DocumentsStagingTable from './DocumentsStagingTable';

const MassiveUploadProject = ({
	column,
	columnStage,
	columnStageCl,
	entityType,
	projectId,
	errors,
	setErrors,
	projectName,
	projectCup,
	isProjectWp,
	hasExternalJobTool = false,
	hasEnabledAi,
	refresh = false,
	setRefresh = () => {},
}) => {
	const { isOe, userAccess, isCoordinator, isPartnerUser } = useContext(UserContext);
	const [optionsPagination, setOptionsPagination] = useState([]);
	const [columnDefs, setColumnDefs] = useState([]);
	const [columnDefsStaging, setColumnDefsStaging] = useState([]);
	const [columnDefsClStaging, setColumnDefsClStaging] = useState([]);
	const [columnDefsErrors, setColumnDefsErrors] = useState([]);
	const [columnDefsDocuments, setColumnDefsDocuments] = useState([]);
	const [subActiveKey, setSubActiveKey] = useState('entities');
	const [sbShow, setSbShow] = useState(<></>);
	const localeText = AG_GRID_LOCALE_IT;
	const gridRef = useRef();
	const gridRefStaging = useRef();
	const gridRefClStaging = useRef();
	const gridRefErrors = useRef();
	const gridRefDocumentsStaging = useRef();

	const hasAccess = useMemo(() => (isProjectWp ? isPartnerUser : isCoordinator), [isProjectWp, isPartnerUser, isCoordinator]);

	const translateEntityType = (entityType) => {
		const textMap = [
			{ entityType: 'contracts', translate: 'Incarichi / Attingimenti', type: 'contracts' },
			{ entityType: 'invoices', translate: 'Fatture', type: 'invoices' },
			{ entityType: 'timesheets', translate: 'Personale', type: 'timesheets' },
			{
				entityType: 'payments',
				translate: 'Pagamenti',
				type: 'invoicepayments',
			},
		];

		let translatedString = textMap.filter((el) => el.entityType === entityType).pop();

		return translatedString;
	};

	const handleStatusBar = useCallback((status, message, timeout) => {
		setSbShow(<StatusBar status={status} message={message} timeout={timeout} />);
	}, []);

	const queryClient = useQueryClient();

	const processingStates = useMemo(() => ['INIT', 'STAGING', 'DOING'], []);

	const batchesQuery = useQuery({
		queryKey: ['batches', projectId],
		queryFn: () => getBatchesByInfo('projects', projectId),
		enabled: !!projectId,

		refetchInterval: (query) => {
			const data = query.state.data;

			if (!data?.success || !data.data?.length) return false;

			const filtered = data.data.filter((el) => el.taskEntity === entityType && el.entityType === 'projects');

			if (!filtered.length) return false;

			const hasProcessing = filtered.some((el) => processingStates.includes(el.status));

			return hasProcessing ? 5000 : false;
		},
	});

	const batchState = useMemo(() => {
		if (!batchesQuery.data?.success) return null;

		const filtered = batchesQuery.data.data.filter((el) => el.taskEntity === entityType && el.entityType === 'projects');

		if (!filtered.length) return null;

		const hasError = filtered.some((el) => el.status === 'ERROR');

		const hasProcessing = filtered.some((el) => processingStates.includes(el.status));

		if (hasError) return 'ERROR';
		if (hasProcessing) return 'DOING';

		return 'DONE';
	}, [batchesQuery.data, entityType, processingStates]);

	useEffect(() => {
		if (!batchState) return;

		if (batchState === 'ERROR') {
			handleStatusBar(
				'error',
				translate('common.error.save', "Si è verificato un errore durante l'elaborazione delle informazioni"),
				false,
			);
		} else {
			setSbShow(<></>);
		}
	}, [batchState, entityType, projectId, queryClient, handleStatusBar, setSbShow]);

	useEffect(() => {
		setOptionsPagination([
			<Fragment key="options">
				<option value="100" key="100">
					100
				</option>
				<option value="500" key="500">
					500
				</option>
				<option defaultValue="1000" key="1000">
					1000
				</option>
			</Fragment>,
		]);
	}, []);

	const LogCellRenderer = useCallback((params) => {
		const red = '#C94723';
		// const yellow = '#D4BE31';
		const green = '#689689';

		const legendStyles = {
			bullet: {
				display: 'inline-block',
				width: '1.2em',
				height: '1.2em',
				borderRadius: '2em',
			},
			label: {
				display: 'inline-block',
				width: '50%',
				lineHeight: '2.5em',
			},
		};

		if (params.value) {
			return (
				<i
					style={{
						...legendStyles.bullet,
						backgroundColor: params.value === 'OK' ? green : red,
						textAlign: 'left',
						verticalAlign: 'sub',
					}}
				/>
			);
		}
	}, []);

	const handleDownloadDocument = useCallback(
		(params) => {
			const handleDownload = async (e) => {
				setSbShow(<></>);
				const result = await downloadDocument(params.data);
				console.log('result', result);
				// if (result.success) {
				// } else {
				// }
			};

			return (
				<OverlayTrigger
					placement="top"
					overlay={
						<Tooltip>
							<OETrans code="common.download" fallback={'Scarica'} />
						</Tooltip>
					}
				>
					<Button variant="link" className="" onClick={(e) => handleDownload(e)}>
						<IcoDownloadFile className="second-color ico-small massive-upload align-bottom" />
					</Button>
				</OverlayTrigger>
			);
		},
		[setSbShow],
	);

	const DocCellRenderer = useCallback((params) => {
		if (params?.data) {
			return (
				<Row className="align-items-center justify-content-center h-100">
					<Col className="col-auto text-center">
						{!params.data.isNew ? (
							<OverlayTrigger
								placement="top"
								overlay={
									<Tooltip>
										<OETrans code="common.docisreplaced" fallback={'Questo documento è stato sovrascritto in data'} />
										&nbsp;{dateFormatter(params.data?.document?.updatedAt)}
									</Tooltip>
								}
							>
								<IcoReplaced
									style={{
										fill:
											// '#689689'
											'var(--np-primary-color)',
									}}
								/>
							</OverlayTrigger>
						) : null}
						{params.data.isNew ? (
							<Badge bg="secondary">
								<OETrans code="common.tooltip.new" fallback={'Nuovo'} />
							</Badge>
						) : null}
						&nbsp;<span className="ms-1">{params.data.fileName}</span>
					</Col>
				</Row>
			);
		}
	}, []);

	useEffect(() => {
		setColumnDefs(column);
		setColumnDefsStaging(columnStage);
		setColumnDefsClStaging(columnStageCl);
		setColumnDefsErrors([
			{
				headerName: translate('common.column', 'Colonna'),
				field: 'colonna',
				filter: 'agTextColumnFilter',
			},
			{
				headerName: translate('common.error', 'Errore'),
				field: 'errore',
				filter: 'agTextColumnFilter',
				minWidth: 500,
				cellStyle: { color: 'red' },
			},
		]);
		setColumnDefsDocuments([
			{
				headerName: '',
				field: 'status',
				cellRenderer: LogCellRenderer,
				filter: 'agTextColumnFilter',
				maxWidth: 100,
				minWidth: 100,
				headerCheckboxSelection: true,
				checkboxSelection: true,
				...columnCentered(),
			},
			{
				headerName: translate('documentsstaging.fileName', 'Nome file'),
				field: 'fileName',
				filter: 'agTextColumnFilter',
				cellRenderer: DocCellRenderer,
				minWidth: 300,
			},
			{
				headerName: translate('documentsstaging.log', 'Messaggi'),
				field: 'log',
				filter: 'agTextColumnFilter',
				minWidth: 400,
				cellStyle: { color: 'red' },
			},
			{
				headerName: translate('documentsstaging.missing', 'Mancante'),
				field: 'missing',
				filter: 'agTextColumnFilter',
				valueGetter: (params) => {
					return params.data.missing ? translate('common.yes', 'Sì') : translate('common.no', 'No');
				},
				maxWidth: 150,
				minWidth: 150,
			},
			{
				headerName: translate('documentsstaging.documentCode', 'Codice documento'),
				field: 'documentCode',
				filter: 'agTextColumnFilter',
				minWidth: 170,
			},
			{
				headerName: '',
				pinned: 'right',
				minWidth: 90,
				maxWidth: 90,
				cellStyle: { textAlign: 'center', padding: 0 },
				cellRenderer: handleDownloadDocument,
			},
		]);
	}, [column, columnStage, columnStageCl, handleDownloadDocument, LogCellRenderer, DocCellRenderer]);

	const defaultColDef = {
		resizable: true,
		suppressMovable: true,
		cellStyle: { padding: '3px 1rem' },
	};

	const onGridReady = useCallback((params, gridRef) => {
		params.api.sizeColumnsToFit();

		window.addEventListener('resize', function () {
			setTimeout(function () {
				params.api.sizeColumnsToFit();
			});
		});

		if (window.screen.width >= 1600) {
			gridRef.current.api.sizeColumnsToFit();
		} else {
			const allColumnIds = [];
			gridRef.current.columnApi.getAllColumns().forEach((column) => {
				allColumnIds.push(column.getId());
			});
			gridRef.current.columnApi.autoSizeColumns(allColumnIds, true);
		}
	}, []);

	const onPageSizeChanged = useCallback((type, ref) => {
		let value = document.getElementById('page-size-' + type).value;
		ref.current.api.paginationSetPageSize(Number(value));
	}, []);

	const ReloadButton = () => {
		return (
			<OverlayTrigger
				placement="top"
				overlay={
					<Tooltip>
						<OETrans code="common.reloaddata" fallback={'Ricarica i dati'} />
					</Tooltip>
				}
			>
				<Button
					onClick={() => {
						queryClient.invalidateQueries({ queryKey: ['batches', projectId] });
						queryClient.invalidateQueries({ queryKey: ['entities', entityType, projectId] });
						queryClient.invalidateQueries({ queryKey: ['staging-entities', entityType, projectId] });
						queryClient.invalidateQueries({ queryKey: ['staging-checklists', entityType, projectId] });
						queryClient.invalidateQueries({ queryKey: ['staging-docs', entityType, projectId] });
					}}
					className="btn custom-ghost-button"
					style={{
						fontWeight: 500,
						fontSize: '18px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '0.5rem',
					}}
				>
					{/* {batchPollingEnabled && 'Elaborazione in corso...'} */}
					{batchesQuery.isFetching || batchState === 'DOING' ? 'Elaborazione in corso...' : null}
					{batchesQuery.isFetching || batchState === 'DOING' ? (
						<Spinner
							as="span"
							className="mx-1"
							animation="border"
							size="sm"
							role="status"
							aria-hidden="true"
							style={{ verticalAlign: 'middle' }}
						/>
					) : (
						<IcoArrowClockwise />
					)}
					<span style={{ translate: '0 1px' }}>Ricarica dati</span>
				</Button>
			</OverlayTrigger>
		);
	};

	const ErrorsTable = () => {
		return (
			<Fragment>
				<Row className="title-simple-box justify-content-center align-items-center mt-4 mb-3">
					<Col className="col-auto p-0">
						<IcoDanger className="me-1 ico-red" />
					</Col>
					<Col className="col-auto p-1">
						<h5 className="m-0">
							<OETrans code="common.errorlist" fallback={'Lista Errori'} />
						</h5>
					</Col>
				</Row>

				<Row className="mb-5">
					<Col className="col-12 col-xxl-9" id="grid-wrapper">
						{Array.isArray(errors) ? (
							<div className="ag-theme-alpine list-table" style={{ width: '100%' }}>
								<AgGridReact
									ref={gridRefErrors}
									columnDefs={columnDefsErrors}
									rowData={errors}
									defaultColDef={defaultColDef}
									domLayout="autoHeight"
									tooltipShowDelay={0}
									tooltipHideDelay={2000}
									tooltipMouseTrack={true}
									onGridReady={(e) => onGridReady(e, gridRefErrors)}
									headerHeight={50}
									rowHeight={50}
									ariaDescription="ag-grid-table"
									suppressCellFocus={true}
									animateRows={true}
									localeText={localeText}
								/>
							</div>
						) : (
							<Loading />
						)}
					</Col>
				</Row>
			</Fragment>
		);
	};

	// const location = useLocation();
	// const activeKey = location.hash ? location.hash.replace('#', '') : '';

	if (hasAccess === undefined || typeof isOe === 'undefined' || typeof userAccess === 'undefined') {
		return <Loading fixedHeight="50vh" />;
	}

	if (isProjectWp) {
		if (!userAccess.isAboveIncludingProjectManager) return <Unauthorized drilled={true} />;
	} else {
		if (!(isOe || userAccess.isOeExternal)) return <Unauthorized drilled={true} />;
	}

	const subNavItems = [
		{
			eventKey: 'entities',
			title: 'Elenco',
			content: (
				<EntitiesTable
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					entityType={entityType}
					gridRef={gridRef}
					localeText={localeText}
					onGridReady={onGridReady}
					subActiveKey={subActiveKey}
					projectId={projectId}
					sbShow={sbShow}
					batchState={batchState}
				/>
			),
		},
		{
			eventKey: 'log-entities',
			title: 'Log Elenco',
			content: (
				<>
					{errors.length > 0 ? <ErrorsTable /> : null}
					<EntitiesStagingTable
						batchState={batchState}
						columnDefs={columnDefsStaging}
						defaultColDef={defaultColDef}
						gridRef={gridRefStaging}
						localeText={localeText}
						onGridReady={onGridReady}
						subActiveKey={subActiveKey}
						projectId={projectId}
						sbShow={sbShow}
						entityType={entityType}
					/>
				</>
			),
		},
		{
			eventKey: 'log-checklists',
			title: 'Log Checklists',
			content: (
				<>
					{errors.length > 0 ? <ErrorsTable /> : null}
					<ChecklistsStagingTable
						batchState={batchState}
						columnDefs={columnDefsClStaging}
						defaultColDef={defaultColDef}
						gridRef={gridRefClStaging}
						localeText={localeText}
						onGridReady={onGridReady}
						subActiveKey={subActiveKey}
						projectId={projectId}
						sbShow={sbShow}
						entityType={entityType}
					/>
				</>
			),
		},
		{
			eventKey: 'log-documents',
			title: 'Log Documenti',
			content: (
				<>
					{errors.length > 0 ? <ErrorsTable /> : null}
					<DocumentsStagingTable
						batchState={batchState}
						columnDefs={columnDefsDocuments}
						defaultColDef={defaultColDef}
						gridRef={gridRefDocumentsStaging}
						localeText={localeText}
						onGridReady={onGridReady}
						subActiveKey={subActiveKey}
						projectId={projectId}
						sbShow={sbShow}
						entityType={entityType}
						handleStatusBar={handleStatusBar}
						setSbShow={setSbShow}
						onPageSizeChanged={onPageSizeChanged}
						optionsPagination={optionsPagination}
					/>
				</>
			),
		},
	];

	return (
		<div style={{ marginTop: '0.5rem' }}>
			{/* <Row id={entityType} className="mt-5 mb-5">
				<Row className="d-flex align-items-center justify-content-start">
					{activeKey === 'timesheets' && hasExternalJobTool ? <ImportJobToolModal /> : null}
				</Row>
			</Row> */}
			<Col className="col-auto ms-auto">
				<ButtonToolbar className="justify-content-between" style={{ margin: '48px 0' }}>
					<ReloadButton />
					<ButtonToolbar className="gap-4 justify-content-between">
						<UploadEntitiesModal entity={translateEntityType(entityType)} hasEnabledAi={hasEnabledAi} />
						<DownloadEntitiesModal entity={translateEntityType(entityType)} />
					</ButtonToolbar>
				</ButtonToolbar>
			</Col>
			<SubTabs
				subNavItems={subNavItems}
				onValueChangeCallback={(key) => {
					setSubActiveKey(key);
					setSbShow(<></>);
					setErrors([]);
				}}
			/>
		</div>
	);
};

export default MassiveUploadProject;
